import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
    wweBundle,
    wweBundle_bundleProductDetail,
    wweBundle_wweBundle_target_configurable,
    wweBundle_wweBundle_target_configurable_ConfigurableProduct,
    wweBundleVariables,
    wweBundle_bundleProductDetail_customisation_estimates,
    wweBundle_bundleProductDetail_setup_charges,
} from 'src/queries/__generated__/wweBundle';
import { ApolloError } from '@apollo/client';
import { Swatch } from 'src/components/ProductDetail/ProductSwatch';
import { Size } from 'src/components/ProductDetail/ProductSizes';
import {
    BaseVariant,
    cleanBundlePrice,
    optionsToColorSwatches,
    optionsToSizes,
} from 'src/components/ProductDetail/utils/product-detail.types';
import { ProductPrice } from 'src/types/global-types';
import { useCurrencyChange } from 'src/components/AppShell/ConfigProvider';
import {
    wearerBundleOptions,
    wearerBundleOptionsVariables,
    wearerBundleOptions_wearerBundleOptions,
} from 'src/queries/__generated__/wearerBundleOptions';
import invariant from 'tiny-invariant';

import bundleProduct from '../../queries/wweBundle.graphql';
import wearerBundleProduct from '../../queries/wearerBundleOptions.graphql';

import {
    BundleOptions,
    bundleOptionsAndVariantsWithStock,
    bundleOptionsFromQuery,
    EMPTY_OPTIONS,
} from './utils/bundleOptionsFromQuery';

export interface WearerSummary {
    image: string;
    name: string;
    id: number;
    sku: string;
    maxQty: number;
    position: number;
}

export type LabelsWithOneBoundleImageProps = { [s: string]: string[] } | string[];

export type ArtworkPriceBreakProps = {
    customisation_estimates: wweBundle_bundleProductDetail_customisation_estimates[];
    setup_charges: wweBundle_bundleProductDetail_setup_charges[];
};

export type ContextType = {
    loading: boolean;
    error: ApolloError | undefined;
    sizes: Size[];
    swatches: Swatch[];
    variants: BaseVariant[];
    bundleOptions: BundleOptions;
    maxQty: number;
    bundlePrice: ProductPrice;
    labelsWithOneBoundleImage: LabelsWithOneBoundleImageProps;
    artworkPriceBreak?: ArtworkPriceBreakProps;
};

const DEFAULT_MAX_QTY = 100;
const EMPTY_ARR = [];
const INITIAL_BUNDLE_PRICE = {
    regular_price_incl: -1,
    regular_price: -1,
    final_price_incl: -1,
    final_price: -1,
    price: -1,
    price_incl: -1,
};

export const BundleOptionsContext = React.createContext<ContextType>({
    loading: true,
    error: undefined,
    sizes: EMPTY_ARR,
    swatches: EMPTY_ARR,
    variants: EMPTY_ARR,
    maxQty: DEFAULT_MAX_QTY,
    bundleOptions: EMPTY_OPTIONS,
    bundlePrice: INITIAL_BUNDLE_PRICE,
    labelsWithOneBoundleImage: EMPTY_ARR,
    artworkPriceBreak: {
        customisation_estimates: [],
        setup_charges: [],
    },
});

type Props = {
    id: string | number;
};

export const BundleOptionsProvider: React.FC<React.PropsWithChildren<Props>> = (props) => {
    /**
     * Make the initial Product Query
     */
    const { loading, error, data, refetch } = useQuery<wweBundle, wweBundleVariables>(bundleProduct, {
        variables: { id: String(props.id), asIntArray: [Number(props.id)] },
        ssr: false,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    });

    /**
     * refetch everything when the currency changes
     */
    useCurrencyChange(refetch);

    const api = useMemo(() => {
        if (!data) return emptyStub(loading, error);
        invariant(data.wweBundle, 'expected data.wweBundle');
        invariant(data.wweBundle.options, 'expected data.wweBundle.options');
        const bundlePrice = cleanBundlePrice(data.bundleProductDetail as wweBundle_bundleProductDetail[]);
        const bundleOptions = bundleOptionsFromQuery(data.wweBundle.options.option_id, data.wweBundle.options.options);
        const artworkPriceBreak = {
            customisation_estimates: data.bundleProductDetail[0]
                ?.customisation_estimates as wweBundle_bundleProductDetail_customisation_estimates[],
            setup_charges: data.bundleProductDetail[0]?.setup_charges as wweBundle_bundleProductDetail_setup_charges[],
        };
        return {
            ...extracted(data.wweBundle, bundleOptions),
            bundlePrice,
            loading,
            error,
            artworkPriceBreak,
        };
    }, [data, error, loading]);

    return <BundleOptionsContext.Provider value={api}>{props.children}</BundleOptionsContext.Provider>;
};

type WearerProps = {
    ids: number[];
    bundle_options: BundleOptions;
    max_qty: number;
};

export const WearerBundleOptionsProvider: React.FC<React.PropsWithChildren<WearerProps>> = (props) => {
    /**
     * Make the initial Product Query
     */
    const { loading, error, data, refetch } = useQuery<wearerBundleOptions, wearerBundleOptionsVariables>(
        wearerBundleProduct,
        {
            variables: { ids: props.ids },
            ssr: false,
            fetchPolicy: 'cache-first',
        },
    );

    /**
     * refetch everything when the currency changes
     */
    useCurrencyChange(refetch);

    const api = useMemo(() => {
        if (!data) return emptyStub(loading, error);
        invariant(data.wearerBundleOptions, 'expected data.wweBundle');
        const first = data.wearerBundleOptions[0];
        invariant(first, 'first item must exist');
        const next = {
            ...extracted({ max_qty: props.max_qty, target_configurable: first }, props.bundle_options),
            bundlePrice: INITIAL_BUNDLE_PRICE,
            loading,
            error,
        };
        return next;
    }, [data, error, loading, props.bundle_options, props.max_qty]);

    return <BundleOptionsContext.Provider value={api}>{props.children}</BundleOptionsContext.Provider>;
};

const emptyStub = (loading: boolean, error: ApolloError | undefined) => ({
    loading,
    error: error,
    sizes: EMPTY_ARR,
    swatches: EMPTY_ARR,
    variants: EMPTY_ARR,
    maxQty: DEFAULT_MAX_QTY,
    bundleOptions: EMPTY_OPTIONS,
    bundlePrice: INITIAL_BUNDLE_PRICE,
    labelsWithOneBoundleImage: EMPTY_ARR,
    artworkPriceBreak: {
        customisation_estimates: [],
        setup_charges: [],
    },
});

type OptionsParams = {
    max_qty: number;
    target_configurable?: wweBundle_wweBundle_target_configurable | wearerBundleOptions_wearerBundleOptions | null;
};

export function extracted(data: OptionsParams, bundleOptions: BundleOptions) {
    const maxQty = data.max_qty ?? DEFAULT_MAX_QTY;
    const item = data.target_configurable;
    const { options, variants, labelsWithOneBoundleImage } = bundleOptionsAndVariantsWithStock(
        item as wweBundle_wweBundle_target_configurable_ConfigurableProduct,
        bundleOptions,
    );
    const swatches: Swatch[] = optionsToColorSwatches(options);
    const sizes: Size[] = optionsToSizes(options);

    return {
        sizes,
        swatches,
        maxQty,
        variants,
        bundleOptions,
        labelsWithOneBoundleImage,
    };
}
